import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertCircle,
  CalendarClock,
  Link2,
  Plus,
  RefreshCw,
  Search,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { LocalizedName, localizedName } from '@/components/localized-name';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { env } from '@/config/env';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  EVENT_TYPE_FILTERS,
  TYPE_DROP_IN,
  eventTypeLabelKey,
} from './event-enums';
import { EditEventSheet } from './edit-event-sheet';
import { EventTimeSlotsSheet } from './event-time-slots-sheet';
import { useEvents, useEventServiceTypes } from './events.api';
import type { BookingEvent } from './types';

/**
 * Booking events — the things customers can reserve a slot in.
 *
 * `GET events/all` returns everything, so all four filters run client-side, as
 * they did in the original. The list defaults to **active only**, which is why
 * the summary line spells out what is being shown: without it a missing event
 * reads as missing data rather than as a filter doing its job.
 */

const ANY = 'ANY';

/** The original's three-way active filter: 0 all, 1 active, 2 inactive. */
const ACTIVE_FILTERS = [
  { value: '0', labelKey: 'events.all' },
  { value: '1', labelKey: 'events.active' },
  { value: '2', labelKey: 'events.inactive' },
] as const;

export function EventsPage() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';

  const { data, isPending, isError, isFetching, refetch } = useEvents();
  const { data: serviceTypes, isPending: loadingServiceTypes } =
    useEventServiceTypes();

  const [eventType, setEventType] = useState(ANY);
  const [serviceTypeId, setServiceTypeId] = useState(ANY);
  const [activeState, setActiveState] = useState('1');
  const [query, setQuery] = useState('');

  const [editing, setEditing] = useState<BookingEvent | null>(null);
  const [creating, setCreating] = useState(false);
  const [slotsFor, setSlotsFor] = useState<BookingEvent | null>(null);

  const events = useMemo(() => data ?? [], [data]);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return events.filter((event) => {
      if (eventType !== ANY && event.type !== Number(eventType)) return false;
      if (serviceTypeId !== ANY && event.serviceTypeId !== serviceTypeId) {
        return false;
      }
      if (activeState === '1' && !event.isActive) return false;
      if (activeState === '2' && event.isActive) return false;
      if (!term) return true;
      // The original searched name, description, location and host.
      return [
        event.name,
        event.description,
        event.location?.name,
        event.location?.description,
        event.host?.firstName,
        event.host?.lastName,
        event.host?.email,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(term);
    });
  }, [events, eventType, serviceTypeId, activeState, query]);

  const stateLabel = t(
    ACTIVE_FILTERS.find((o) => o.value === activeState)?.labelKey ??
      'events.all'
  ).toLowerCase();

  const filterFields: FilterField[] = [
    {
      id: 'type',
      label: t('events.typeLabel'),
      value: eventType,
      onChange: setEventType,
      options: [
        { value: ANY, label: t('events.any') },
        ...EVENT_TYPE_FILTERS.map((option) => ({
          value: String(option.value),
          label: t(option.labelKey),
        })),
      ],
    },
    {
      id: 'serviceType',
      label: t('events.serviceType'),
      value: serviceTypeId,
      onChange: setServiceTypeId,
      disabled: loadingServiceTypes,
      options: [
        { value: ANY, label: t('events.any') },
        ...(serviceTypes ?? []).map((serviceType) => ({
          value: serviceType.id,
          label: localizedName(serviceType, language),
        })),
      ],
    },
    {
      id: 'active',
      label: t('events.isActive'),
      value: activeState,
      onChange: setActiveState,
      // All / Active / Inactive is an order that means something.
      sort: false,
      resetValue: '0',
      options: ACTIVE_FILTERS.map((option) => ({
        value: option.value,
        label: t(option.labelKey),
      })),
    },
  ];

  function copyLink(event: BookingEvent) {
    const url = `${env.bookingEventUrl}/${event.id}`;
    void navigator.clipboard
      ?.writeText(url)
      .then(() => toast.success(t('events.linkCopied')))
      .catch(() => toast.error(t('events.copyFailed')));
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('events.title')}
        description={t('events.subtitle')}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void refetch()}
              disabled={isFetching}
            >
              <RefreshCw className={cn(isFetching && 'animate-spin')} />
              <span className="hidden sm:inline">{t('common.reload')}</span>
            </Button>
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus />
              {t('events.createNew')}
            </Button>
          </div>
        }
      />

      <FilterBar
        busy={isFetching}
        search={
          <div className="relative">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('events.searchPlaceholder')}
              aria-label={t('common.search')}
              className="h-10 pl-9"
            />
          </div>
        }
        fields={filterFields}
        summary={
          <p className="text-muted-foreground text-sm">
            {t('events.showingCount', {
              count: filtered.length,
              state: stateLabel,
            })}
          </p>
        }
      />

      {isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('events.loadFailed')}</p>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {isPending && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 p-3">
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-52" />
                <Skeleton className="h-3 w-36" />
              </div>
              <Skeleton className="h-5 w-16" />
            </div>
          ))}
        </div>
      )}

      {data && filtered.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={isFetching} />
          <p
            className={cn(
              'text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm',
              isFetching && 'opacity-60 transition-opacity'
            )}
          >
            {events.length === 0 ? t('events.noEvents') : t('events.noMatches')}
          </p>
        </div>
      )}

      {filtered.length > 0 && (
        <>
          <div className="hidden rounded-xl border lg:block">
            <Table busy={isFetching}>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{t('events.name')}</TableHead>
                  <TableHead>{t('events.location')}</TableHead>
                  <TableHead>{t('events.host')}</TableHead>
                  <TableHead>{t('events.typeLabel')}</TableHead>
                  <TableHead>{t('events.serviceType')}</TableHead>
                  <TableHead className="text-right">
                    {t('events.price')}
                  </TableHead>
                  <TableHead className="text-center">DL</TableHead>
                  <TableHead className="text-right">
                    {t('events.actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        {event.imageUrl && (
                          <img
                            src={event.imageUrl}
                            alt=""
                            className="bg-muted size-10 shrink-0 rounded object-cover"
                          />
                        )}
                        <LocalizedName
                          nameEn={event.nameEn}
                          nameEs={event.nameEs}
                          internalName={event.name}
                        />
                      </div>
                    </TableCell>
                    <TableCell>{event.location?.name ?? '—'}</TableCell>
                    <TableCell>
                      {event.host
                        ? [event.host.firstName, event.host.lastName]
                            .filter(Boolean)
                            .join(' ')
                        : '—'}
                    </TableCell>
                    <TableCell>{t(eventTypeLabelKey(event.type))}</TableCell>
                    <TableCell>
                      {event.serviceType
                        ? localizedName(event.serviceType, language)
                        : '—'}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">
                      {formatCurrency(event.price)}
                    </TableCell>
                    <TableCell className="text-center">
                      {/* Only drop-in events have a public page to link to. */}
                      <Button
                        variant="outline"
                        size="icon-sm"
                        aria-label={t('events.copyLink')}
                        title={t('events.copyLink')}
                        disabled={event.type !== TYPE_DROP_IN}
                        onClick={() => copyLink(event)}
                      >
                        <Link2 className="size-3.5" />
                      </Button>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditing(event)}
                        >
                          {t('events.edit')}
                        </Button>
                        <Button size="sm" onClick={() => setSlotsFor(event)}>
                          <CalendarClock className="size-3.5" />
                          {t('events.timeSlots')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="relative space-y-2 lg:hidden">
            <BusyOverlay show={isFetching} />
            {filtered.map((event) => (
              <div
                key={event.id}
                className={cn(
                  'bg-card space-y-3 rounded-xl border p-3',
                  isFetching && 'opacity-60 transition-opacity'
                )}
              >
                <div className="flex items-start gap-3">
                  {event.imageUrl && (
                    <img
                      src={event.imageUrl}
                      alt=""
                      className="bg-muted size-12 shrink-0 rounded object-cover"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <LocalizedName
                      nameEn={event.nameEn}
                      nameEs={event.nameEs}
                      internalName={event.name}
                    />
                    {event.location?.name && (
                      <p className="text-muted-foreground truncate text-xs">
                        {event.location.name}
                      </p>
                    )}
                    {event.host && (
                      <p className="text-muted-foreground truncate text-xs">
                        {[event.host.firstName, event.host.lastName]
                          .filter(Boolean)
                          .join(' ')}
                      </p>
                    )}
                  </div>
                  <span className="shrink-0 font-semibold tabular-nums">
                    {formatCurrency(event.price)}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  <Badge variant={event.isActive ? 'success' : 'secondary'}>
                    {event.isActive ? t('events.active') : t('events.inactive')}
                  </Badge>
                  <span className="text-muted-foreground text-xs">
                    {t('events.typeLabel')}: {t(eventTypeLabelKey(event.type))}
                  </span>
                  {event.serviceType && (
                    <span className="text-muted-foreground text-xs">
                      {t('events.serviceType')}:{' '}
                      {localizedName(event.serviceType, language)}
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    className="flex-1"
                    onClick={() => setEditing(event)}
                  >
                    {t('events.edit')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => setSlotsFor(event)}
                  >
                    <CalendarClock className="size-3.5" />
                    {t('events.timeSlots')}
                  </Button>
                  {event.type === TYPE_DROP_IN && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyLink(event)}
                    >
                      <Link2 className="size-3.5" />
                      {t('events.copyLink')}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <EditEventSheet
        event={editing}
        open={editing !== null || creating}
        onOpenChange={(next) => {
          if (!next) {
            setEditing(null);
            setCreating(false);
          }
        }}
      />

      <EventTimeSlotsSheet
        event={slotsFor}
        open={slotsFor !== null}
        onOpenChange={(next) => {
          if (!next) setSlotsFor(null);
        }}
      />
    </div>
  );
}
